import { create } from 'zustand';
import {
  applyResult,
  emptyMastery,
  masteryKey,
  type MasteryState,
  type MasterySkill,
} from '@ezishizi/learning-core';
import { useSession } from '../state/session.ts';
import { store } from './store/index.ts';
import type { AttemptRecord, ChallengeStats, SessionRecord } from './store/index.ts';

/**
 * Saves a finished session in the background (see Play.tsx, PracticeRunner.tsx, StoryPlayer.tsx):
 * every one of them scores the session, calls `setLastResult`, calls `startBackgroundSave`, and
 * only then navigates to /result — none of them waits on the save to finish first, because a slow
 * or offline save must never leave the child staring at a spinner waiting on a round trip to the
 * API.
 *
 * What gets saved is a plain-data job (the steps below, keyed by the session's id) kept in
 * localStorage under `ezishizi:outbox`, written before the first request goes out. A step is
 * marked done as soon as the server has it, and a job is deleted only when every step is done, so
 * a failed save is never lost: starting the next activity, closing the tab or reloading the page
 * leaves the job where it is, and it is retried until the server acknowledges it — at app start,
 * when the browser comes back online, when the tab becomes visible again, whenever another
 * session is saved, and from the Result screen's Retry button (`flushOutbox`, `retryJob`).
 * Which session failed is tracked per id in `useOutbox`, so a late failure of an earlier save
 * can't flag the session the child is looking at now.
 *
 * Only one run per job id is ever in flight (a second trigger joins it): `recordDay` adds to the
 * day's totals, so running a step twice would credit it twice. Steps that already succeeded are
 * skipped on every later pass for the same reason; `saveSession` is insert-or-ignore on its
 * client-generated ids, so resending it is always safe.
 */

export type SaveStepInput =
  /** Mastery rows computed up front (Play, which holds the live map). */
  | { kind: 'mastery'; rows: MasteryState[] }
  /** Results to fold into whatever mastery the server holds right now (StoryPlayer). Run once:
   * the computed rows replace this step in the stored job before they are upserted, so a retry
   * after a lost response sends the same rows again instead of applying the results a second
   * time on top of an upsert that had already landed. */
  | {
      kind: 'mastery_from_results';
      results: Array<{ character: string; skill: MasterySkill; correct: boolean }>;
      /** ISO time the session finished, the clock `applyResult` schedules the next review from. */
      at: string;
    }
  | { kind: 'session'; session: SessionRecord; attempts: AttemptRecord[] }
  | {
      kind: 'day';
      day: string;
      delta: { stars: number; points: number; minutes: number };
      goal: number;
    }
  | { kind: 'challenge'; day: string };

export type SaveStep = SaveStepInput & { done: boolean };

export interface SaveJob {
  /** The session id; also what `LastResult.sessionId` points at. */
  id: string;
  profileId: string;
  createdAt: string;
  steps: SaveStep[];
}

/** Which jobs' last attempt failed, by session id; read by the Result screen. */
export const useOutbox = create<{ failed: Record<string, true> }>(() => ({ failed: {} }));

function setFailed(id: string, failed: boolean) {
  const cur = useOutbox.getState().failed;
  if (failed === id in cur) return;
  if (failed) {
    useOutbox.setState({ failed: { ...cur, [id]: true } });
  } else {
    const { [id]: _gone, ...rest } = cur;
    useOutbox.setState({ failed: rest });
  }
}

const STORAGE_KEY = 'ezishizi:outbox';

// localStorage is the point (it survives a reload and a closed tab on an iPad; sessionStorage
// doesn't), but every call is guarded: it throws in Safari private mode, and doesn't exist at all
// in non-browser test environments. When it can't be used the jobs live in `memory` instead, so
// the retries still work until the page goes away.
let memory: Record<string, SaveJob> = {};
let storageBroken = false;

function readAll(): Record<string, SaveJob> {
  if (storageBroken || typeof localStorage === 'undefined') return memory;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, SaveJob>) : {};
  } catch {
    return memory;
  }
}

function writeAll(jobs: Record<string, SaveJob>) {
  memory = jobs;
  if (storageBroken || typeof localStorage === 'undefined') return;
  try {
    if (Object.keys(jobs).length) localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Full or blocked: stop trusting it, so a later read doesn't hand back an out-of-date copy.
    storageBroken = true;
  }
}

function updateJob(id: string, change: (job: SaveJob) => SaveJob | null) {
  const jobs = { ...readAll() };
  const job = jobs[id];
  if (!job) return;
  const next = change(job);
  if (next) jobs[id] = next;
  else delete jobs[id];
  writeAll(jobs);
}

/** Only the Play-style challenge tally is ever handed back to a screen, and only while that
 * screen's own session is still the one on show. */
const challengeCallbacks = new Map<string, (stats: ChallengeStats) => void>();

async function runStep(job: SaveJob, i: number): Promise<void> {
  const step = job.steps[i]!;
  switch (step.kind) {
    case 'mastery_from_results': {
      const rows = await store.getMastery(job.profileId);
      const map = new Map(rows.map((r) => [masteryKey(r.character, r.skill), r]));
      const touched = new Map<string, MasteryState>();
      const at = new Date(step.at);
      for (const r of step.results) {
        const key = masteryKey(r.character, r.skill);
        const next = applyResult(map.get(key) ?? emptyMastery(r.character, r.skill), r.correct, at);
        map.set(key, next);
        touched.set(key, next);
      }
      // Durable before the upsert goes out; the next pass then runs it as a plain `mastery` step.
      updateJob(job.id, (j) => ({
        ...j,
        steps: j.steps.map((s, n) =>
          n === i ? { kind: 'mastery', rows: [...touched.values()], done: false } : s,
        ),
      }));
      return;
    }
    case 'mastery':
      await store.upsertMastery(job.profileId, step.rows);
      break;
    case 'session':
      await store.saveSession(step.session, step.attempts);
      break;
    case 'day':
      await store.recordDay(job.profileId, step.day, step.delta, step.goal);
      break;
    case 'challenge': {
      const stats = await store.recordChallenge(job.profileId, step.day);
      // Skipped for a job drained after a reload, or once the child is on to another session.
      if (useSession.getState().lastResult?.sessionId === job.id) {
        challengeCallbacks.get(job.id)?.(stats);
      }
      break;
    }
  }
  updateJob(job.id, (j) => ({
    ...j,
    steps: j.steps.map((s, n) => (n === i ? { ...s, done: true } : s)),
  }));
}

const running = new Map<string, Promise<boolean>>();

/** Runs one job's remaining steps in order; true once it is complete (and removed). */
function runJob(id: string): Promise<boolean> {
  const inFlight = running.get(id);
  if (inFlight) return inFlight;
  const run = (async () => {
    // Yield first, so `running.set` below has registered this run before its `finally` can fire.
    await null;
    setFailed(id, false);
    try {
      for (;;) {
        // Re-read every time: this is what says which steps are really done.
        const job = readAll()[id];
        if (!job) return true;
        const i = job.steps.findIndex((s) => !s.done);
        if (i === -1) {
          updateJob(id, () => null);
          return true;
        }
        await runStep(job, i);
      }
    } catch (e) {
      console.error('Failed to save session', e);
      setFailed(id, true);
      return false;
    } finally {
      running.delete(id);
      challengeCallbacks.delete(id);
    }
  })();
  running.set(id, run);
  return run;
}

/** Retries every pending job, oldest first to start (they run side by side, so one stuck request
 * can't hold up the rest). Never throws. */
export async function flushOutbox(): Promise<void> {
  const ids = Object.values(readAll())
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((j) => j.id);
  await Promise.all(ids.map((id) => runJob(id)));
}

/** The Result screen's Retry: this session first, then anything else still waiting. */
export async function retryJob(id: string): Promise<void> {
  const first = runJob(id);
  await Promise.all([first, flushOutbox()]);
}

/**
 * Stores the job, then starts it without waiting for it. Older pending jobs are retried too.
 * `onChallengeStats` is an in-memory extra: it is handed the daily-challenge tally if the screen
 * that built the job is still showing this session when it arrives.
 */
export function startBackgroundSave(
  job: { id: string; profileId: string; steps: SaveStepInput[] },
  opts: { onChallengeStats?: (stats: ChallengeStats) => void } = {},
): void {
  try {
    const stored: SaveJob = {
      id: job.id,
      profileId: job.profileId,
      createdAt: new Date().toISOString(),
      steps: job.steps.map((s) => ({ ...s, done: false })),
    };
    writeAll({ ...readAll(), [job.id]: stored });
    if (opts.onChallengeStats) challengeCallbacks.set(job.id, opts.onChallengeStats);
    void runJob(job.id);
    void flushOutbox();
  } catch (e) {
    console.error('Failed to save session', e);
    setFailed(job.id, true);
  }
}

/** Drain whatever an earlier visit left behind, and again whenever there is reason to think the
 * network is back. Called once at app start. */
export function startOutboxRetries(): void {
  void flushOutbox();
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => void flushOutbox());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flushOutbox();
  });
}
